import React, { useCallback, useEffect, useRef, useState } from 'react';
import CompanyMasthead from './CompanyMasthead';
import ProjectRotator from './ProjectRotator';
import ValuesList from './ValuesList';
import TestimonialCard from './TestimonialCard';
import WhatHappensNext from './WhatHappensNext';
import { useEngagementBreakpoint } from './useMediaQuery';
import './CandidateEngagementPanel.css';

/** Panel-level copy (rail, mobile pill, overlays). */
export const ENGAGEMENT_PANEL_SHELL_COPY = {
  railLabel: "While you're here",
  mobilePillLabel: 'About SmartRabbitz',
  drawerCloseLabel: 'Close panel',
  sheetCloseLabel: 'Close',
  overlayTitle: 'About SmartRabbitz'
};

function useFocusTrap(active, containerRef) {
  useEffect(() => {
    if (!active || !containerRef.current) return undefined;
    const root = containerRef.current;
    const selector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const getFocusable = () => Array.from(root.querySelectorAll(selector));
    const focusables = getFocusable();
    focusables[0]?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') return;
      if (e.key !== 'Tab') return;
      const items = getFocusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    root.addEventListener('keydown', onKeyDown);
    return () => root.removeEventListener('keydown', onKeyDown);
  }, [active, containerRef]);
}

function PanelScroll({ initialProjectId }) {
  return (
    <>
      <CompanyMasthead />
      <ProjectRotator initialProjectId={initialProjectId} />
      <ValuesList />
      <TestimonialCard />
    </>
  );
}

function PanelBody({ initialProjectId, timelineStep, progressPulseToken, scrollClassName = '' }) {
  return (
    <>
      <div className={`cep-panel__scroll ${scrollClassName}`.trim()}>
        <PanelScroll initialProjectId={initialProjectId} />
      </div>
      <WhatHappensNext activeStep={timelineStep} pulseToken={progressPulseToken} />
    </>
  );
}

/**
 * Side panel for candidates already in the hiring pipeline (Clarity Loop form).
 *
 * @param {{
 *   onProgressUpdate?: (progress: { step: number }) => void;
 *   progressPulseToken?: number;
 *   hideOnMobile?: boolean;
 *   initialProjectId?: number;
 * }} props
 *
 * onProgressUpdate: optional; invoke from the parent when form/session progress changes
 * so the timeline can advance in a future iteration (panel keeps internal step until then).
 */
export default function CandidateEngagementPanel({
  onProgressUpdate,
  progressPulseToken = 0,
  hideOnMobile = false,
  initialProjectId
}) {
  const { isMobile, isTablet, isLaptop, isDesktop } = useEngagementBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [timelineStep] = useState(1);
  const drawerRef = useRef(null);
  const sheetRef = useRef(null);
  const showSidebar = isDesktop || isLaptop;
  const showRail = isTablet;
  const showMobileChrome = isMobile && !hideOnMobile;

  useFocusTrap(drawerOpen, drawerRef);
  useFocusTrap(sheetOpen, sheetRef);

  const closeOverlays = useCallback(() => {
    setDrawerOpen(false);
    setSheetOpen(false);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') closeOverlays();
    };
    if (drawerOpen || sheetOpen) {
      document.addEventListener('keydown', onKey);
      document.body.classList.add('cep-overlay-open');
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('cep-overlay-open');
    };
  }, [drawerOpen, sheetOpen, closeOverlays]);

  if (!showSidebar && !showRail && !showMobileChrome) {
    return null;
  }

  const panelBodyProps = { initialProjectId, timelineStep, progressPulseToken };

  return (
    <>
      {showSidebar && (
        <aside
          className={`cep-panel cep-panel--sidebar${isLaptop ? ' cep-panel--laptop' : ' cep-panel--desktop'}`}
          aria-label={ENGAGEMENT_PANEL_SHELL_COPY.overlayTitle}
        >
          <PanelBody {...panelBodyProps} />
        </aside>
      )}

      {showRail && (
        <>
          <button
            type="button"
            className="cep-rail"
            onClick={() => setDrawerOpen(true)}
            aria-expanded={drawerOpen}
            aria-controls="cep-drawer"
          >
            <span className="cep-rail__text">{ENGAGEMENT_PANEL_SHELL_COPY.railLabel}</span>
          </button>
          {drawerOpen && (
            <div className="cep-overlay" role="presentation">
              <button
                type="button"
                className="cep-overlay__backdrop"
                aria-label={ENGAGEMENT_PANEL_SHELL_COPY.drawerCloseLabel}
                onClick={() => setDrawerOpen(false)}
              />
              <aside
                id="cep-drawer"
                ref={drawerRef}
                className="cep-drawer"
                aria-label={ENGAGEMENT_PANEL_SHELL_COPY.overlayTitle}
                role="dialog"
                aria-modal="true"
              >
                <button
                  type="button"
                  className="cep-drawer__close"
                  onClick={() => setDrawerOpen(false)}
                >
                  {ENGAGEMENT_PANEL_SHELL_COPY.drawerCloseLabel}
                </button>
                <PanelBody {...panelBodyProps} scrollClassName="cep-panel__scroll--drawer" />
              </aside>
            </div>
          )}
        </>
      )}

      {showMobileChrome && (
        <>
          <button
            type="button"
            className="cep-mobile-pill"
            onClick={() => setSheetOpen(true)}
            aria-expanded={sheetOpen}
            aria-controls="cep-sheet"
          >
            {ENGAGEMENT_PANEL_SHELL_COPY.mobilePillLabel}
          </button>
          {sheetOpen && (
            <div className="cep-overlay cep-overlay--sheet" role="presentation">
              <button
                type="button"
                className="cep-overlay__backdrop"
                aria-label={ENGAGEMENT_PANEL_SHELL_COPY.sheetCloseLabel}
                onClick={() => setSheetOpen(false)}
              />
              <div
                id="cep-sheet"
                ref={sheetRef}
                className="cep-sheet"
                role="dialog"
                aria-modal="true"
                aria-label={ENGAGEMENT_PANEL_SHELL_COPY.overlayTitle}
              >
                <header className="cep-sheet__head">
                  <h2 className="cep-sheet__title">{ENGAGEMENT_PANEL_SHELL_COPY.overlayTitle}</h2>
                  <button type="button" className="cep-sheet__close" onClick={() => setSheetOpen(false)}>
                    {ENGAGEMENT_PANEL_SHELL_COPY.sheetCloseLabel}
                  </button>
                </header>
                <PanelBody {...panelBodyProps} scrollClassName="cep-panel__scroll--sheet" />
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
